import moment from 'moment-timezone';
import $ from 'legacy/jquery';
import t from 'shared/utils/translation';
import Tooltip from 'legacy/tooltip';
import './availability_scheduling_window.css';

const FLYOUT_BODY_MARKER = 'has-scheduling-window-flyout';
const HOVER_TARGET_SELECTOR =
  '.fc-bgevent.out-of-scheduling-window-bg, .fc-day-header.out-of-scheduling-window';
const HOVER_DELAY_MS = 100;
const TOOLTIP_BELOW_HEADER_PX = 10;
const BLOCKED_EVENT_DEFAULTS = {
  rendering: 'inverse-background',
  _blockedByWindow: true,
  className: 'out-of-scheduling-window-bg',
};

export default class AvailabilitySchedulingWindow {
  constructor(container, timeZoneId) {
    this.container = container;
    this.timeZoneId = timeZoneId;
    this.start = ceilToHalfHour(
      moment(container.data('scheduling-window-start'))
    );
    this.end = floorToHalfHour(moment(container.data('scheduling-window-end')));
    this._initialized = false;
    this._showTimer = null;
  }

  static fromContainer(container, timeZoneId) {
    const start = container.data('scheduling-window-start');
    const end = container.data('scheduling-window-end');
    if (!start || !end) {
      return null;
    }

    return new AvailabilitySchedulingWindow(container, timeZoneId);
  }

  applyToOptions(options) {
    const windowRange = { start: this.start, end: this.end };
    options.viewRender = () => this.styleCells();
    options.selectConstraint = windowRange;
    options.eventConstraint = windowRange;

    const existingSelect = options.select;
    options.select = (start, end, jsEvent, view) => {
      if (existingSelect) {
        return existingSelect(start, end, jsEvent, view);
      }
    };
    options.eventAfterRender = (event, element) => {
      if (event._blockedByWindow) {
        element.attr(
          'data-description',
          t(
            'candidate_profile.availability_modal.scheduling_window.unavailable_tooltip'
          )
        );
      }
    };
  }

  initInteractions() {
    if (this._initialized) return;
    this._initialized = true;

    document.body.classList.add(FLYOUT_BODY_MARKER);
    this.ensureTooltipFlyoutMounted();
    this.bindTooltipHoverHandlers();
    this.styleCells();
  }

  ensureTooltipFlyoutMounted() {
    if (!Tooltip.flyout().length) {
      $('body').append($(Tooltip.flyoutHtml));
    }
  }

  bindTooltipHoverHandlers() {
    this.container.on('mouseenter', HOVER_TARGET_SELECTOR, (event) => {
      const target = event.currentTarget;
      this.clearShowTimer();
      this._showTimer = setTimeout(() => {
        this.showTooltipAtColumnHeader(target);
      }, HOVER_DELAY_MS);
    });

    this.container.on('mouseleave', HOVER_TARGET_SELECTOR, () => {
      this.clearShowTimer();
      Tooltip.flyout().hide();
    });
  }

  clearShowTimer() {
    if (this._showTimer) {
      clearTimeout(this._showTimer);
      this._showTimer = null;
    }
  }

  showTooltipAtColumnHeader(target) {
    const $target = $(target);
    const text = $target.attr('data-description');
    if (!text) return;

    const $header = this.findColumnHeader($target);
    if (!$header || !$header.length) return;

    Tooltip.setText(text);
    const $flyout = Tooltip.flyout();
    const headerOffset = $header.offset();
    $flyout.css({
      top: headerOffset.top + $header.outerHeight() + TOOLTIP_BELOW_HEADER_PX,
      left:
        headerOffset.left + $header.outerWidth() / 2 - $flyout.outerWidth() / 2,
    });
    $flyout.show();
  }

  findColumnHeader($target) {
    if ($target.hasClass('fc-day-header')) {
      return $target;
    }
    const $td = $target.closest('td');
    if (!$td.length) return $();
    const colIndex = $td.prevAll('td:not(.fc-axis)').length;
    return this.container.find('.fc-day-header').eq(colIndex);
  }

  styleCells() {
    const windowStartDate = this.start.format('YYYY-MM-DD');
    const windowEndDate = this.end.format('YYYY-MM-DD');
    const text = t(
      'candidate_profile.availability_modal.scheduling_window.unavailable_tooltip'
    );

    this.container.find('.fc-day-header, .fc-day').each(function () {
      const $cell = $(this);
      const dateStr = $cell.attr('data-date');
      if (!dateStr) return;

      const isOutOfWindow =
        dateStr < windowStartDate || dateStr > windowEndDate;
      $cell.toggleClass('out-of-scheduling-window', isOutOfWindow);

      if (!$cell.hasClass('fc-day-header')) return;

      if (isOutOfWindow) {
        $cell.attr('data-description', text);
      } else {
        $cell.removeAttr('data-description');
      }
    });

    this.addOutOfScheduleWindowBgEvents();
  }

  addOutOfScheduleWindowBgEvents() {
    this.removeExistingBlockedEvents();
    this.container.fullCalendar('addEventSource', [
      buildBlockedEvent(this.start, this.end),
    ]);
  }

  removeExistingBlockedEvents() {
    this.container.fullCalendar(
      'removeEvents',
      (event) => event._blockedByWindow === true
    );
  }
}

function buildBlockedEvent(start, end) {
  return { start, end, ...BLOCKED_EVENT_DEFAULTS };
}

// Candidate availability is only selectable along 30 minute boundaries,
// so snap the window to 30 minutes so there are no unselectable gaps.
const HALF_HOUR_MS = 30 * 60 * 1000;

const ceilToHalfHour = (m) =>
  moment(Math.ceil(m.valueOf() / HALF_HOUR_MS) * HALF_HOUR_MS);

const floorToHalfHour = (m) =>
  moment(Math.floor(m.valueOf() / HALF_HOUR_MS) * HALF_HOUR_MS);
